import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Database, Upload, BarChart2, Search, Filter,
  X, CheckCircle, AlertCircle, Clock, ChevronDown,
  FileText, Loader2, UploadCloud, Users, TrendingUp,
  RefreshCw, Download, Eye, MapPin, Briefcase, Zap,
  Info, XCircle
} from "lucide-react";
import { USER_API_END_POINT } from "@/utils/constant";

// ─── Tabs ────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "search",    label: "Search Database", icon: Search },
  { id: "upload",    label: "Bulk Upload",      icon: UploadCloud },
  { id: "analytics", label: "Analytics",        icon: BarChart2 },
];

// ─── Mock / seed data for demonstration ──────────────────────────────────────
const SEED_CANDIDATES = [
  { id: 1, name: "Arjun Mehta",    title: "Full Stack Developer",  exp: "5 yrs", location: "Bangalore, IN", skills: ["React", "Node.js", "MongoDB"],         source: "internal", visa: "Citizen",           viewed: false },
  { id: 2, name: "Priya Sharma",   title: "ML Engineer",           exp: "3 yrs", location: "Mumbai, IN",    skills: ["Python", "TensorFlow", "AWS"],          source: "uploaded", visa: "Citizen",           viewed: true  },
  { id: 3, name: "Rahul Verma",    title: "DevOps Engineer",       exp: "7 yrs", location: "Pune, IN",      skills: ["Kubernetes", "Docker", "CI/CD"],        source: "internal", visa: "Work_Visa",         viewed: false },
  { id: 4, name: "Sneha Patel",    title: "Product Designer",      exp: "4 yrs", location: "Delhi, IN",     skills: ["Figma", "UX Research", "Prototyping"],  source: "uploaded", visa: "Citizen",           viewed: false },
  { id: 5, name: "Vikram Singh",   title: "Data Scientist",        exp: "6 yrs", location: "Hyderabad, IN", skills: ["SQL", "Python", "Tableau"],             source: "internal", visa: "Permanent_Resident", viewed: true  },
  { id: 6, name: "Ananya Reddy",   title: "Backend Engineer",      exp: "2 yrs", location: "Chennai, IN",   skills: ["Java", "Spring Boot", "PostgreSQL"],    source: "uploaded", visa: "Student_Visa",      viewed: false },
  { id: 7, name: "Karan Joshi",    title: "Android Developer",     exp: "4 yrs", location: "Noida, IN",     skills: ["Kotlin", "Jetpack Compose", "Firebase"], source: "internal", visa: "Citizen",          viewed: false },
  { id: 8, name: "Divya Nair",     title: "QA Engineer",           exp: "3 yrs", location: "Kochi, IN",     skills: ["Selenium", "Cypress", "Jest"],          source: "uploaded", visa: "Citizen",           viewed: true  },
];

const VISA_OPTS = [
  "all", "Citizen", "Permanent_Resident", "Work_Visa", "Student_Visa", "Other",
];
const EXP_OPTS = ["all", "0-2 yrs", "3-5 yrs", "6-10 yrs", "10+ yrs"];

// ─── Avatar colour pool ───────────────────────────────────────────────────────
const AV_COLORS = [
  { bg: "#dbeafe", color: "#1d4ed8" },
  { bg: "#dcfce7", color: "#166534" },
  { bg: "#ede9fe", color: "#5b21b6" },
  { bg: "#fff7ed", color: "#c2410c" },
  { bg: "#fce7f3", color: "#9d174d" },
  { bg: "#f0fdf4", color: "#14532d" },
];
const avColor = (name) => AV_COLORS[name.charCodeAt(0) % AV_COLORS.length];
const initials = (name) => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

// ─── File status helpers ──────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  if (status === "success")
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
        <CheckCircle className="w-2.5 h-2.5" /> Saved
      </span>
    );
  if (status === "processing")
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
        <Loader2 className="w-2.5 h-2.5 animate-spin" /> Parsing…
      </span>
    );
  if (status === "failed")
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
        <AlertCircle className="w-2.5 h-2.5" /> Failed
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
      <Clock className="w-2.5 h-2.5" /> Queued
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const InternalDBPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Tabs
  const [activeTab, setActiveTab] = useState("search");

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [visaFilter, setVisaFilter] = useState("all");
  const [expFilter, setExpFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [candidates] = useState(SEED_CANDIDATES);

  // Upload state
  const [fileQueue, setFileQueue] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);

  // ── Filtering ────────────────────────────────────────────────────────────
  const filtered = candidates.filter((c) => {
    const q = searchQuery.toLowerCase();
    const matchQ =
      !q ||
      c.name.toLowerCase().includes(q) ||
      c.title.toLowerCase().includes(q) ||
      c.location.toLowerCase().includes(q) ||
      c.skills.some((s) => s.toLowerCase().includes(q));
    const matchSrc = sourceFilter === "all" || c.source === sourceFilter;
    const matchVisa = visaFilter === "all" || c.visa === visaFilter;
    return matchQ && matchSrc && matchVisa;
  });

  const activeFilterCount = [
    sourceFilter !== "all",
    visaFilter !== "all",
    expFilter !== "all",
  ].filter(Boolean).length;

  // ── File upload handlers ──────────────────────────────────────────────────
  const addFiles = useCallback((files) => {
    const newEntries = Array.from(files).map((f) => ({
      id: Math.random().toString(36).slice(2),
      file: f,
      name: f.name,
      size: f.size,
      status: "queued",
      progress: 0,
    }));
    setFileQueue((prev) => [...prev, ...newEntries]);
    setUploadSummary(null);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const removeFile = (id) =>
    setFileQueue((prev) => prev.filter((f) => f.id !== id));

  const clearQueue = () => {
    setFileQueue([]);
    setUploadSummary(null);
  };

  const handleUpload = async () => {
    const pending = fileQueue.filter((f) => f.status === "queued");
    if (!pending.length) return;

    setIsUploading(true);
    const formData = new FormData();
    pending.forEach((f) => formData.append("resumes", f.file));

    // Set all pending → processing
    setFileQueue((prev) =>
      prev.map((f) =>
        f.status === "queued" ? { ...f, status: "processing", progress: 30 } : f
      )
    );

    try {
      const res = await fetch(`${USER_API_END_POINT}/bulk-upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await res.json();

      // Map API results back to queue entries
      const resultMap = {};
      (data.results || []).forEach((r) => {
        resultMap[r.file] = r.status;
      });

      setFileQueue((prev) =>
        prev.map((f) => {
          if (f.status !== "processing") return f;
          const apiStatus = resultMap[f.name];
          return {
            ...f,
            status: apiStatus === "success" ? "success" : "failed",
            progress: apiStatus === "success" ? 100 : 0,
          };
        })
      );

      const success = (data.results || []).filter((r) => r.status === "success").length;
      const failed  = (data.results || []).filter((r) => r.status !== "success").length;
      setUploadSummary({ success, failed, total: data.results?.length || 0 });
    } catch (err) {
      setFileQueue((prev) =>
        prev.map((f) =>
          f.status === "processing" ? { ...f, status: "failed", progress: 0 } : f
        )
      );
      setUploadSummary({ success: 0, failed: pending.length, total: pending.length });
    } finally {
      setIsUploading(false);
    }
  };

  const formatSize = (bytes) =>
    bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(0)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f8f9fc]">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="relative w-full overflow-hidden">
        <div className="absolute inset-0 bg-[#012760]" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-400 rounded-full opacity-10 blur-3xl" />
        <div className="absolute -bottom-10 right-20 w-80 h-80 bg-blue-300 rounded-full opacity-10 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-12">
          {/* Back */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/50 hover:text-white text-xs font-medium mb-5 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Candidates
          </button>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">
                  Internal DB
                </span>
                <span className="w-1 h-1 rounded-full bg-blue-400 opacity-60" />
                <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">
                  Resume Intelligence
                </span>
              </div>
              <h1 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2">
                Resume{" "}
                <span className="text-blue-300">Intelligence</span> Hub
              </h1>
              <p className="text-white/50 text-sm max-w-xl">
                Search, filter and manage your proprietary resume database. Bulk upload resumes — they're auto-parsed and instantly searchable.
              </p>
            </div>

            {/* Stats pills */}
            <div className="flex gap-3 flex-wrap">
              {[
                { icon: Users,       label: "247 Resumes",    bg: "bg-white/10" },
                { icon: CheckCircle, label: "241 Parsed",     bg: "bg-green-500/20" },
                { icon: AlertCircle, label: "6 Failed",       bg: "bg-red-500/20" },
              ].map(({ icon: Icon, label, bg }) => (
                <div
                  key={label}
                  className={`flex items-center gap-2 ${bg} border border-white/10 rounded-xl px-4 py-2`}
                >
                  <Icon className="w-3.5 h-3.5 text-white/60" />
                  <span className="text-white text-xs font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab bar ───────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 flex gap-0">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap
                ${activeTab === id
                  ? "border-[#012760] text-[#012760]"
                  : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200"
                }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {id === "upload" && fileQueue.filter((f) => f.status === "queued").length > 0 && (
                <span className="ml-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                  {fileQueue.filter((f) => f.status === "queued").length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Page body ─────────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-8">

        {/* ════════════════════════════════════════════════════════════════
            TAB: Search
        ════════════════════════════════════════════════════════════════ */}
        {activeTab === "search" && (
          <div>
            {/* Search bar row */}
            <div className="flex gap-3 mb-4 flex-wrap">
              <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm focus-within:border-[#012760] transition-colors">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search name, title, skills, location…"
                  className="w-full text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")}>
                    <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all shadow-sm
                  ${showFilters || activeFilterCount > 0
                    ? "border-[#012760] text-[#012760] bg-[#012760]/5"
                    : "border-gray-200 text-gray-600 bg-white hover:border-[#012760] hover:text-[#012760]"
                  }`}
              >
                <Filter className="w-3.5 h-3.5" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="w-4 h-4 rounded-full bg-[#012760] text-white text-[9px] font-bold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
              </button>

              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all">
                <Download className="w-3.5 h-3.5" />
                Export
              </button>
            </div>

            {/* Filter panel */}
            {showFilters && (
              <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-5 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {/* Source */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Source</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: "all",      label: "All"       },
                        { value: "internal", label: "Internal DB" },
                        { value: "uploaded", label: "Uploaded"  },
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => setSourceFilter(value)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all
                            ${sourceFilter === value
                              ? "bg-[#012760] text-white border-[#012760]"
                              : "bg-white text-gray-600 border-gray-200 hover:border-[#012760]"
                            }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Visa */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Visa Status</p>
                    <select
                      value={visaFilter}
                      onChange={(e) => setVisaFilter(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg text-sm px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#012760]/20 focus:border-[#012760]"
                    >
                      {VISA_OPTS.map((v) => (
                        <option key={v} value={v}>
                          {v === "all" ? "All Visa Types" : v.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Experience */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Experience</p>
                    <select
                      value={expFilter}
                      onChange={(e) => setExpFilter(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg text-sm px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#012760]/20 focus:border-[#012760]"
                    >
                      {EXP_OPTS.map((v) => (
                        <option key={v} value={v}>
                          {v === "all" ? "Any Experience" : v}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {activeFilterCount > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setSourceFilter("all");
                        setVisaFilter("all");
                        setExpFilter("all");
                      }}
                      className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 font-medium"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Results header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-gray-700">
                {filtered.length.toLocaleString()}{" "}
                <span className="font-normal text-gray-400">
                  {filtered.length === 1 ? "resume" : "resumes"} found
                </span>
              </p>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#012760] opacity-60" />
                <span className="text-xs text-gray-400">Internal DB</span>
                <span className="w-2 h-2 rounded-full bg-blue-400 opacity-60" />
                <span className="text-xs text-gray-400">Uploaded</span>
              </div>
            </div>

            {/* Candidate list */}
            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-gray-700 font-semibold mb-1">No resumes found</p>
                <p className="text-gray-400 text-sm">Try adjusting your search or clearing filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((c) => {
                  const av = avColor(c.name);
                  return (
                    <div
                      key={c.id}
                      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-200 overflow-hidden"
                    >
                      <div className="flex items-center gap-4 p-4">
                        {/* Avatar */}
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
                          style={{ background: av.bg, color: av.color }}
                        >
                          {initials(c.name)}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-semibold text-gray-800">{c.name}</p>
                            {c.viewed && (
                              <span className="flex items-center gap-1 text-[10px] font-medium text-blue-500 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-full">
                                <Eye className="w-2.5 h-2.5" /> Viewed
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <Briefcase className="w-3 h-3" /> {c.title}
                            </span>
                            <span className="text-gray-300">·</span>
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <MapPin className="w-3 h-3" /> {c.location}
                            </span>
                          </div>
                          <div className="flex gap-1.5 flex-wrap">
                            {c.skills.map((s) => (
                              <span
                                key={s}
                                className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
                              >
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Right meta */}
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border
                              ${c.source === "internal"
                                ? "bg-[#f0f4ff] border-[#dde6ff] text-[#3730a3]"
                                : "bg-gray-50 border-gray-200 text-gray-500"
                              }`}
                          >
                            {c.source === "internal" ? "Internal DB" : "Uploaded"}
                          </span>
                          <span className="text-xs text-gray-400">{c.exp}</span>
                          <button className="text-[11px] text-[#012760] border border-[#012760] rounded-lg px-3 py-1 hover:bg-[#012760] hover:text-white transition-all font-medium">
                            View Profile
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            TAB: Upload
        ════════════════════════════════════════════════════════════════ */}
        {activeTab === "upload" && (
          <div className="max-w-3xl mx-auto">

            {/* Upload summary banner */}
            {uploadSummary && (
              <div
                className={`flex items-center gap-3 rounded-2xl px-5 py-4 mb-6 border
                  ${uploadSummary.failed === 0
                    ? "bg-green-50 border-green-200"
                    : "bg-yellow-50 border-yellow-200"
                  }`}
              >
                {uploadSummary.failed === 0
                  ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  : <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                }
                <div>
                  <p className={`text-sm font-semibold ${uploadSummary.failed === 0 ? "text-green-800" : "text-yellow-800"}`}>
                    Upload complete — {uploadSummary.success} saved, {uploadSummary.failed} failed
                  </p>
                  <p className={`text-xs mt-0.5 ${uploadSummary.failed === 0 ? "text-green-600" : "text-yellow-600"}`}>
                    Parsed resumes are now searchable in the Database tab.
                  </p>
                </div>
              </div>
            )}

            {/* Drop zone */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm mb-5">
              <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#012760]">
                  <UploadCloud className="w-4 h-4" />
                  Bulk Resume Upload
                </div>
                <div className="flex gap-2">
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3]">
                    247 in DB
                  </span>
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-700">
                    12 this week
                  </span>
                </div>
              </div>

              {/* Drop area */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`m-5 rounded-xl border-2 border-dashed p-12 text-center cursor-pointer transition-all
                  ${isDragging
                    ? "border-[#012760] bg-[#f0f4ff]"
                    : "border-gray-200 bg-gray-50 hover:border-[#012760] hover:bg-[#f0f4ff]"
                  }`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#012760] flex items-center justify-center mx-auto mb-4">
                  <UploadCloud className="w-6 h-6 text-white" />
                </div>
                <p className="text-base font-semibold text-gray-800 mb-1">
                  Drop resumes here or click to browse
                </p>
                <p className="text-sm text-gray-400 mb-4">
                  Each resume is parsed by AI and saved to your database automatically
                </p>
                <div className="flex gap-2 justify-center flex-wrap mb-5">
                  {["PDF", "DOCX", "DOC", "TXT"].map((fmt) => (
                    <span
                      key={fmt}
                      className="bg-white border border-gray-200 rounded-md px-2.5 py-0.5 text-xs font-medium text-gray-500"
                    >
                      {fmt}
                    </span>
                  ))}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
                >
                  <Upload className="w-4 h-4" />
                  Choose Files
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  accept=".pdf,.doc,.docx,.txt"
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </div>

              {/* File queue */}
              {fileQueue.length > 0 && (
                <div className="mx-5 mb-5 space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Queue ({fileQueue.length})
                    </p>
                    <button
                      onClick={clearQueue}
                      className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Clear all
                    </button>
                  </div>

                  {fileQueue.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#012760] flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate">{f.name}</p>
                        <p className="text-[10px] text-gray-400">{formatSize(f.size)}</p>
                        {f.status === "processing" && (
                          <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#012760] to-blue-400 rounded-full transition-all duration-500"
                              style={{ width: `${f.progress}%` }}
                            />
                          </div>
                        )}
                      </div>
                      <StatusBadge status={f.status} />
                      {f.status === "queued" && (
                        <button
                          onClick={() => removeFile(f.id)}
                          className="text-gray-300 hover:text-red-400 transition-colors ml-1"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload action */}
            {fileQueue.filter((f) => f.status === "queued").length > 0 && (
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#012760] text-white text-sm font-semibold rounded-xl hover:bg-[#023080] transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
              >
                {isUploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Uploading & Parsing…</>
                ) : (
                  <><Zap className="w-4 h-4" /> Upload {fileQueue.filter((f) => f.status === "queued").length} Resume{fileQueue.filter((f) => f.status === "queued").length > 1 ? "s" : ""}</>
                )}
              </button>
            )}

            {/* Info card */}
            <div className="mt-5 flex gap-3 bg-[#f0f4ff] border border-[#dde6ff] rounded-2xl px-5 py-4">
              <Info className="w-4 h-4 text-[#3730a3] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[#3730a3] mb-1">How bulk upload works</p>
                <p className="text-xs text-[#4338ca] leading-relaxed">
                  Each resume is uploaded to S3, then an AI parser extracts name, skills, experience,
                  job title, and contact info — and saves it to your internal database.
                  Parsed candidates are instantly searchable from the Search tab.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            TAB: Analytics
        ════════════════════════════════════════════════════════════════ */}
        {activeTab === "analytics" && (
          <div>
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Total Resumes",      value: "247",   sub: "in database",       icon: Database,     color: "text-[#012760]", bg: "bg-[#f0f4ff]" },
                { label: "Parsed Successfully", value: "241",  sub: "97.6% success",     icon: CheckCircle,  color: "text-green-700", bg: "bg-green-50"  },
                { label: "Failed Uploads",      value: "6",    sub: "2.4% failure rate", icon: AlertCircle,  color: "text-red-600",   bg: "bg-red-50"    },
                { label: "Viewed This Month",   value: "38",   sub: "by your team",      icon: Eye,          color: "text-purple-700", bg: "bg-purple-50" },
              ].map(({ label, value, sub, icon: Icon, color, bg }) => (
                <div key={label} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                  <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center mb-3`}>
                    <Icon className={`w-4.5 h-4.5 ${color}`} style={{ width: 18, height: 18 }} />
                  </div>
                  <p className="text-2xl font-bold text-gray-800 mb-0.5">{value}</p>
                  <p className="text-xs text-gray-400">{label}</p>
                  <p className="text-[11px] text-gray-300 mt-1">{sub}</p>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {/* Top skills */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp className="w-4 h-4 text-[#012760]" />
                  <p className="text-sm font-semibold text-[#012760]">Top Skills in Database</p>
                </div>
                <div className="space-y-4">
                  {[
                    { skill: "React / Next.js",   count: 89, pct: 72, color: "bg-[#012760]"  },
                    { skill: "Python / ML",        count: 74, pct: 60, color: "bg-blue-500"   },
                    { skill: "Node.js / Express",  count: 61, pct: 50, color: "bg-indigo-500" },
                    { skill: "AWS / DevOps",       count: 48, pct: 39, color: "bg-orange-400" },
                    { skill: "Java / Spring Boot", count: 35, pct: 29, color: "bg-purple-500" },
                  ].map(({ skill, count, pct, color }) => (
                    <div key={skill}>
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-gray-700 font-medium">{skill}</span>
                        <span className="text-gray-400">{count} resumes</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upload history */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-5">
                  <RefreshCw className="w-4 h-4 text-[#012760]" />
                  <p className="text-sm font-semibold text-[#012760]">Recent Upload Activity</p>
                </div>
                <div className="space-y-3">
                  {[
                    { label: "Today",          count: 4,  status: "All parsed"     },
                    { label: "Yesterday",      count: 8,  status: "7 parsed, 1 failed" },
                    { label: "This week",      count: 12, status: "All parsed"     },
                    { label: "Last week",      count: 21, status: "19 parsed, 2 failed" },
                    { label: "This month",     count: 47, status: "45 parsed, 2 failed" },
                  ].map(({ label, count, status }) => (
                    <div key={label} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-gray-600">{label}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400">{status}</span>
                        <span className="text-sm font-semibold text-[#012760] min-w-[2rem] text-right">+{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalDBPage;
